// Shared ESP32 connect / detect / flash logic. Used by OTA Management (one
// goggle, manual connect) and Factory Flash (many goggles, auto-detected), so
// both flash with the same offsets and steps.
import { downloadFirmwareFile, downloadFirmwareFromUrl } from './storageService';

// Both REKKIE goggle variants run on an ESP32-S3; they differ by in-package
// PSRAM, which esptool reads from the eFuse PSRAM capability field:
//   ESP32-S3, no embedded PSRAM       -> magnetic_25   (2026 MY)
//   ESP32-S3FH4R2, 2 MB embedded PSRAM -> display_v2_26 (2027 MY)
// Anything else (other chips, other PSRAM sizes) is an unsupported device.
export const SUPPORTED_CHIP = 'ESP32-S3';
const PSRAM_CAP_TO_VARIANT = {
  0: 'magnetic_25',
  2: 'display_v2_26'
};

// Every variant, in display order.
export const VARIANTS = ['magnetic_25', 'display_v2_26'];

// Human-readable label per variant.
export const VARIANT_LABELS = {
  magnetic_25: '2026 MY REKKIE Goggles',
  display_v2_26: '2027 MY REKKIE Goggles'
};

// Short label per variant, for cards and compact rows.
export const VARIANT_SHORT_LABELS = {
  magnetic_25: '2026 MY',
  display_v2_26: '2027 MY'
};

// Flash offsets per variant, from the board partition CSVs in Goggles-OS
// (partitions/ESP32S3/s3_partitions.csv and s3_partitions_4mb.csv). Both put
// the bootloader at 0x0 and app0 at 0x10000; the 4 MB table differs only in
// slot sizes, which ship in that variant's own partitions.bin. Flashing the app
// at the wrong offset bricks the device, so this stays per-variant.
export const FLASH_ADDRESSES = {
  magnetic_25:   { BOOTLOADER: 0x0, PARTITIONS: 0x8000, FIRMWARE: 0x10000 },
  display_v2_26: { BOOTLOADER: 0x0, PARTITIONS: 0x8000, FIRMWARE: 0x10000 }
};

// USB vendor IDs of serial bridges a goggle can show up as.
export const SERIAL_PORT_FILTERS = [
  { usbVendorId: 0x303A }, // Espressif USB VID
  { usbVendorId: 0x10C4 }, // Silicon Labs CP210x
  { usbVendorId: 0x1A86 }, // CH340
  { usbVendorId: 0x0403 }, // FTDI
];

export const isGogglePort = (port) => {
  const { usbVendorId } = port.getInfo();
  return SERIAL_PORT_FILTERS.some(f => f.usbVendorId === usbVendorId);
};

// First firmware version, per variant, that prints the REKKIE-BOOT line at
// startup (see parseBootLine). Factory Flash reboots every goggle after
// flashing. For builds at or above this version it confirms the reboot by MAC
// and version, and an unconfirmed reboot is an error. For older builds it can
// only recognise the reboot by the app boot banner. Goggles-OS prints the
// line from 1.8.12 on. null would mean no build of that variant prints it.
export const BOOT_LINE_MIN_VERSION = {
  magnetic_25: { majorVersion: 1, minorVersion: 8, patchVersion: 12, buildNumVersion: 0 },
  display_v2_26: { majorVersion: 1, minorVersion: 8, patchVersion: 12, buildNumVersion: 0 }
};

// esptool-js status lines that confuse operators more than they help.
const NOISY_TERMINAL_LINES = new Set(['Leaving...', 'Hard resetting via RTS pin...']);

// Resolve the connected device's firmware variant, or null if unsupported.
// Only an ESP32-S3 can be a REKKIE goggle; the PSRAM eFuse tells the two apart.
export const detectVariant = async (loader) => {
  if (loader.chip.CHIP_NAME !== SUPPORTED_CHIP) return { variant: null, psramCap: null };
  const psramCap = await loader.chip.getPsramCap(loader);
  return { variant: PSRAM_CAP_TO_VARIANT[psramCap] || null, psramCap };
};

// Format version from a manifest's manifestFirmware.
export const formatVersion = (manifestFirmware) => {
  if (!manifestFirmware) return 'Unknown';
  const { majorVersion, minorVersion, patchVersion, buildNumVersion } = manifestFirmware;
  return `v${majorVersion || 0}.${minorVersion || 0}.${patchVersion || 0}.${buildNumVersion || 0}`;
};

const versionParts = (manifestFirmware) => {
  const fw = manifestFirmware || {};
  return [fw.majorVersion || 0, fw.minorVersion || 0, fw.patchVersion || 0, fw.buildNumVersion || 0];
};

// Compare two manifests by version (negative if a > b), highest first.
// Order: major -> minor -> patch -> build.
export const compareManifestVersions = (a, b) => {
  const av = versionParts(a.manifestFirmware);
  const bv = versionParts(b.manifestFirmware);
  for (let i = 0; i < 4; i++) {
    if (av[i] !== bv[i]) return bv[i] - av[i];
  }
  return 0;
};

// Base channels (public, beta, alpha) are capitalized as-is.
// Feature channels (feature_*) strip the prefix, replace underscores with spaces, and title-case.
export const formatChannelName = (channel) => {
  if (channel.startsWith('feature_')) {
    return channel
      .slice('feature_'.length)
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return channel.charAt(0).toUpperCase() + channel.slice(1);
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// esptool-js returns the MAC as an "aa:bb:cc:dd:ee:ff" string; older versions
// returned bytes. Normalize either to lowercase colon-separated hex.
const formatMac = (mac) => {
  if (!mac) return null;
  if (typeof mac === 'string') return mac.toLowerCase();
  return Array.from(mac).map(b => b.toString(16).padStart(2, '0')).join(':');
};

// esptool-js wants each image as a binary string. Converting is slow for a
// multi-MB app, so callers do it once per image and reuse the result.
const toBinaryString = (bytes) => {
  let out = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return out;
};

/** Close an esptool-js transport, ignoring errors (the port may already be gone). */
export const closeTransport = async (transport) => {
  if (!transport) return;
  try {
    await transport.disconnect();
  } catch (e) {
    // Port already closed or unplugged.
  }
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Reset the chip out of the flasher stub into whatever is in flash, the way
 * esptool.py's default `--after hard_reset` does: assert RTS (EN low), hold
 * 100 ms, release. esptool-js 0.5.7's own HardReset only releases RTS, which
 * is a no-op because connecting already left it released, so connectAndDetect
 * installs this in its place (resetConstructors.hardReset).
 * The goggle's USB-Serial-JTAG port usually drops and re-enumerates as the
 * chip reboots, so the caller must treat the transport as gone afterwards.
 */
export const hardResetChip = async (transport) => {
  await transport.setRTS(true);
  await sleep(100);
  await transport.setRTS(false);
};

/**
 * Reboot a freshly flashed goggle into its new firmware and release the port.
 * Never throws: the port can vanish mid-reset, which is the expected outcome.
 * @returns {Promise<boolean>} false if the reset signal could not be sent
 */
export const rebootIntoApp = async (loader, transport) => {
  let sent = true;
  try {
    await loader.after('hard_reset');
  } catch (e) {
    sent = false;
  }
  await closeTransport(transport);
  return sent;
};

/**
 * Open a serial port with esptool-js, sync with the bootloader, and identify
 * the chip. Leaves the port open and the chip in the flasher stub.
 * @param {SerialPort} port - an already-permitted Web Serial port
 * @param {(message: string, type?: string) => void} log
 * @returns {Promise<{loader, transport, chip: string, mac: string|null, variant: string|null, psramCap: number|null}>}
 * On failure the port is closed before the error is rethrown.
 */
export const connectAndDetect = async (port, log) => {
  const { ESPLoader, Transport } = await import('esptool-js');
  const transport = new Transport(port);

  const forward = (data) => {
    const msg = data && data.trim();
    if (msg && !NOISY_TERMINAL_LINES.has(msg)) log(msg, 'info');
  };
  const terminal = { clean: () => {}, writeLine: forward, write: forward };

  const loader = new ESPLoader({
    transport,
    baudrate: 460800,
    terminal,
    romBaudrate: 115200,
    enableTracing: false,
    resetConstructors: {
      hardReset: (t) => ({ reset: () => hardResetChip(t) })
    }
  });

  try {
    await loader.main();

    let mac = null;
    try {
      mac = formatMac(await loader.chip.readMac(loader));
    } catch (e) {
      log('Could not read MAC address', 'warning');
    }

    let variant = null;
    let psramCap = null;
    try {
      ({ variant, psramCap } = await detectVariant(loader));
    } catch (e) {
      log(`Could not read PSRAM eFuse: ${e.message}`, 'warning');
    }

    return { loader, transport, chip: loader.chip.CHIP_NAME, mac, variant, psramCap };
  } catch (error) {
    await closeTransport(transport);
    throw error;
  }
};

/**
 * Download the bootloader, partition table and app for one variant and
 * prepare them for flashing.
 * @param {string} variant
 * @param {object} manifest - channel manifest entry with linkToFw
 * @param {(message: string, type?: string) => void} log
 * @returns {Promise<Array<{name: string, address: number, size: number, data: string}>>}
 */
export const loadFirmwareImages = async (variant, manifest, log) => {
  const addresses = FLASH_ADDRESSES[variant];
  if (!addresses) throw new Error(`No flash offsets for variant ${variant}`);

  const sources = [
    { name: 'Bootloader', address: addresses.BOOTLOADER, fetch: () => downloadFirmwareFile('bootloader', variant), file: 'bootloader.bin' },
    { name: 'Partitions', address: addresses.PARTITIONS, fetch: () => downloadFirmwareFile('partitions', variant), file: 'partitions.bin' },
    { name: 'Firmware', address: addresses.FIRMWARE, fetch: () => downloadFirmwareFromUrl(manifest.linkToFw), file: `firmware ${formatVersion(manifest.manifestFirmware)}` }
  ];

  const images = [];
  for (const source of sources) {
    log(`Downloading ${source.file} (${VARIANT_SHORT_LABELS[variant] || variant}) from server...`, 'info');
    const bytes = await source.fetch();
    if (!bytes) throw new Error(`Failed to download ${source.file} for ${VARIANT_SHORT_LABELS[variant] || variant}`);
    log(`${source.name}: ${formatFileSize(bytes.length)}`, 'info');
    images.push({ name: source.name, address: source.address, size: bytes.length, data: toBinaryString(bytes) });
  }
  return images;
};

/**
 * Erase the chip and write every image.
 * @param {object} loader - connected ESPLoader
 * @param {Array} images - from loadFirmwareImages
 * @param {{onStage?: (stage: 'erasing'|'flashing', imageName?: string) => void,
 *          onProgress?: (percent: number) => void,
 *          log: (message: string, type?: string) => void}} callbacks
 */
export const flashImages = async (loader, images, { onStage, onProgress, log }) => {
  onStage?.('erasing');
  log('Erasing flash memory (this may take a moment)...', 'warning');
  await loader.eraseFlash();
  log('Flash memory erased successfully', 'success');

  const totalSize = images.reduce((sum, image) => sum + image.size, 0);
  let bytesWritten = 0;

  for (const image of images) {
    onStage?.('flashing', image.name);
    log(`Flashing ${image.name} at 0x${image.address.toString(16)}...`, 'info');
    const baseBytes = bytesWritten;
    try {
      await loader.writeFlash({
        fileArray: [{ data: image.data, address: image.address }],
        flashSize: 'keep',
        flashMode: 'keep',
        flashFreq: 'keep',
        eraseAll: false,
        compress: true,
        reportProgress: (_fileIndex, written) => {
          onProgress?.(((baseBytes + written) / totalSize) * 100);
        }
      });
    } catch (error) {
      throw new Error(`Failed to flash ${image.name}: ${error.message}`);
    }
    bytesWritten += image.size;
    log(`${image.name} flashed successfully`, 'success');
  }
  onProgress?.(100);
};

/**
 * Parse the startup line Goggles-OS prints (repeatedly, for ~20 s after boot):
 *   REKKIE-BOOT fw=1.8.7.0 mac=a4:cf:12:34:56:3c hw=display_v2_26
 * Matches anywhere in the text, so log prefixes and color codes are fine.
 * @returns {{version: string, mac: string, hw: string}|null} version as "v1.8.7.0"
 */
export const parseBootLine = (text) => {
  const match = /REKKIE-BOOT fw=(\d+\.\d+\.\d+\.\d+) mac=([0-9a-fA-F]{2}(?::[0-9a-fA-F]{2}){5}) hw=([A-Za-z0-9_]+)/.exec(text);
  if (!match) return null;
  return { version: `v${match[1]}`, mac: match[2].toLowerCase(), hw: match[3] };
};

/** True if the given build of this variant prints the REKKIE-BOOT line. */
export const supportsBootLine = (variant, manifestFirmware) => {
  const min = BOOT_LINE_MIN_VERSION[variant];
  if (!min) return false;
  const have = versionParts(manifestFirmware);
  const need = versionParts(min);
  for (let i = 0; i < 4; i++) {
    if (have[i] !== need[i]) return have[i] > need[i];
  }
  return true;
};

// Goggles-OS app boot banner (src/main.c serial_task), printed once per boot
// over USB serial. Present in every current build; it carries no MAC or
// version, so on its own it only says "some goggle just booted the app".
const APP_BOOT_BANNER = /Type 0=menu/;

/** True if the text contains the Goggles-OS app boot banner. */
export const hasAppBootBanner = (text) => APP_BOOT_BANNER.test(text);

/**
 * Open a port without resetting the chip and read until a REKKIE-BOOT line
 * arrives or the timeout passes, noting whether the app boot banner went by.
 * Leaves the port open either way; the caller closes it (or hands it to
 * connectAndDetect after closing).
 * @returns {Promise<{boot: {version: string, mac: string, hw: string}|null, banner: boolean}>}
 *   boot: the parsed REKKIE-BOOT line, if one arrived (identifies the goggle).
 *   banner: true if the app boot banner arrived (a goggle just booted).
 */
export const listenForBoot = async (port, timeoutMs) => {
  const result = { boot: null, banner: false };
  await port.open({ baudRate: 115200 });
  // DTR/RTS drive the chip's reset and boot pins; hold both released so
  // opening the port doesn't reset the goggle we're trying to listen to.
  try {
    await port.setSignals({ dataTerminalReady: false, requestToSend: false });
  } catch (e) {
    // Some bridges don't support setting signals.
  }
  if (!port.readable) return result;

  const reader = port.readable.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const timer = setTimeout(() => { reader.cancel().catch(() => {}); }, timeoutMs);
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return result;
      buffer += decoder.decode(value, { stream: true });
      if (hasAppBootBanner(buffer)) result.banner = true;
      result.boot = parseBootLine(buffer);
      // The REKKIE-BOOT line identifies the goggle, so stop as soon as it
      // arrives. A banner alone keeps listening in case the line follows.
      if (result.boot) return result;
      if (buffer.length > 16384) buffer = buffer.slice(-4096);
    }
  } catch (e) {
    return result;
  } finally {
    clearTimeout(timer);
    try { reader.releaseLock(); } catch (e) { /* already released */ }
  }
};

/** Close a raw Web Serial port, ignoring errors. */
export const closePort = async (port) => {
  try {
    await port.close();
  } catch (e) {
    // Already closed or unplugged.
  }
};
